/**
 * Gráficos de «Indicadores» con SVG propio (sin librería, §21): la línea del periodo con su área suave, la del periodo con
 * el que se compara discontinua y los puntos incompletos huecos (G425). `Minigrafico` es la línea de 7.2 en cada tarjeta.
 */
export interface PuntoGrafico {
    etiqueta: string;
    valor: number | null;
    incompleto: boolean;
}

const ANCHO = 640;

function escala(maximo: number): number {
    if (maximo <= 0) {
        return 1;
    }
    const paso = 10 ** Math.floor(Math.log10(maximo));
    for (const m of [1, 2, 2.5, 5, 10]) {
        if (m * paso >= maximo) {
            return m * paso;
        }
    }
    return 10 * paso;
}

function trazo(valores: readonly (number | null)[], alto: number, tope: number, n: number): string {
    let d = '';
    let dentro = false;
    valores.forEach((v, i) => {
        if (v === null) {
            dentro = false;
            return;
        }
        const x = n <= 1 ? ANCHO / 2 : (i / (n - 1)) * ANCHO;
        const y = alto - (v / tope) * alto;
        d += `${dentro ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)} `;
        dentro = true;
    });
    return d.trim();
}

export function Grafico({ puntos, anteriores, escribir, etiqueta }: { puntos: readonly PuntoGrafico[]; anteriores?: readonly (number | null)[]; escribir: (v: number) => string; etiqueta: string }) {
    const alto = 200;
    const valores = puntos.map((p) => p.valor);
    const previos = anteriores ?? [];
    const tope = escala(Math.max(0, ...valores.filter((v): v is number => v !== null), ...previos.filter((v): v is number => v !== null)));
    const n = Math.max(puntos.length, previos.length);
    const linea = trazo(valores, alto, tope, n);
    const ultimo = valores.reduce<number>((u, v, i) => (v === null ? u : i), -1);
    const area = linea === '' || ultimo < 0 ? '' : `${linea} L${((ultimo / Math.max(1, n - 1)) * ANCHO).toFixed(1)},${String(alto)} L${(((valores.findIndex((v) => v !== null)) / Math.max(1, n - 1)) * ANCHO).toFixed(1)},${String(alto)} Z`;
    const marcas = [0, 0.25, 0.5, 0.75, 1].map((f) => tope * f);
    const cada = Math.max(1, Math.ceil(puntos.length / 6));
    return (
        <div className="rv-ind-grafico">
            <div className="rv-ind-grafico__ejes" aria-hidden="true">
                {[...marcas].reverse().map((m) => (
                    <span key={m}>{escribir(m)}</span>
                ))}
            </div>
            <div className="rv-ind-grafico__lienzo">
                <svg viewBox={`0 0 ${String(ANCHO)} ${String(alto)}`} preserveAspectRatio="none" role="img" aria-label={etiqueta}>
                    {marcas.map((m) => (
                        <line key={m} x1="0" x2={ANCHO} y1={alto - (m / tope) * alto} y2={alto - (m / tope) * alto} className="rv-ind-grafico__rejilla" />
                    ))}
                    {area !== '' && <path d={area} className="rv-ind-grafico__area" />}
                    {previos.length > 0 && <path d={trazo(previos, alto, tope, n)} className="rv-ind-grafico__antes" />}
                    {linea !== '' && <path d={linea} className="rv-ind-grafico__ahora" />}
                </svg>
                {puntos.map((p, i) =>
                    p.incompleto ? (
                        <span
                            key={p.etiqueta}
                            className="rv-ind-grafico__hueco"
                            title={`${p.etiqueta}: sin datos completos`}
                            style={{ left: `${String(n <= 1 ? 50 : (i / (n - 1)) * 100)}%`, top: `${String(p.valor === null ? 100 : 100 - (p.valor / tope) * 100)}%` }}
                        />
                    ) : null,
                )}
                <div className="rv-ind-grafico__fechas" aria-hidden="true">
                    {puntos.map((p, i) => (i % cada === 0 || i === puntos.length - 1 ? <span key={p.etiqueta} style={{ left: `${String(n <= 1 ? 50 : (i / (n - 1)) * 100)}%` }}>{p.etiqueta}</span> : null))}
                </div>
            </div>
        </div>
    );
}

/** La línea pequeña de cada tarjeta, sin ejes. */
export function Minigrafico({ valores }: { valores: readonly (number | null)[] }) {
    const alto = 40;
    const tope = escala(Math.max(0, ...valores.filter((v): v is number => v !== null)));
    const d = trazo(valores, alto, tope, valores.length);
    if (d === '') {
        return null;
    }
    return (
        <svg className="rv-ind-mini" viewBox={`0 0 ${String(ANCHO)} ${String(alto)}`} preserveAspectRatio="none" aria-hidden="true">
            <path d={d} />
        </svg>
    );
}

/** Barras horizontales del desglose: el total de cada valor de la dimensión sobre el mayor. */
export function Barras({ filas }: { filas: readonly { clave: string; nombre: string; valor: number | null; cifra: string; detalle: string | null }[] }) {
    const maximo = Math.max(0, ...filas.map((f) => f.valor ?? 0));
    return (
        <ul className="rv-ind-barras">
            {filas.map((f) => (
                <li key={f.clave}>
                    <span className="rv-ind-barras__nombre">{f.nombre}</span>
                    <span className="rv-ind-barras__cifra">
                        <strong>{f.cifra}</strong>
                        {f.detalle !== null && <small>{f.detalle}</small>}
                    </span>
                    <i style={{ width: `${String(maximo > 0 ? ((f.valor ?? 0) / maximo) * 100 : 0)}%` }} />
                </li>
            ))}
        </ul>
    );
}
